import {
  aggregateAll,
  extractSecaoFromKey,
  extractZonaFromKey,
  getPoliticalPartyName,
  normalizeCandidateName,
  normalizeCargoKey,
  normalizeIdentityValue,
  normalizePoliticalLabel,
  normalizeText,
} from './painel-data.js';
import {
  DEFAULT_PARTY_CATALOG,
} from './painel-state.js';

export function findHistoricalCandidateMatch(candidates = [], targetCandidate = {}, options = {}) {
  const rows = Array.isArray(candidates) ? candidates : [];
  if (!targetCandidate || !rows.length) return null;

  const partidosCatalog = options.partidosCatalog || DEFAULT_PARTY_CATALOG;
  const targetName = normalizeCandidateName(targetCandidate.nome);
  const targetCanonical = targetCandidate.canonicalIdentity || targetCandidate.identidade_canonica || {};
  const targetCanonicalId = normalizeIdentityValue(targetCandidate.canonicalId || targetCanonical.id);
  const targetOfficialId = normalizeIdentityValue(targetCandidate.officialId || targetCanonical.id_oficial_tse);
  const targetNumber = String(targetCandidate.numero || '').trim();
  const targetParty = normalizePoliticalLabel(getPoliticalPartyName(targetCandidate, partidosCatalog), '');
  const targetCargo = normalizeCargoKey(targetCandidate.cargoKey || targetCandidate.cargo);

  const scored = rows
    .map(candidate => {
      let score = 0;
      const reasons = [];
      const candidateName = normalizeCandidateName(candidate.nome);
      const candidateCanonical = candidate.canonicalIdentity || candidate.identidade_canonica || {};
      const candidateCanonicalId = normalizeIdentityValue(candidate.canonicalId || candidateCanonical.id);
      const candidateOfficialId = normalizeIdentityValue(candidate.officialId || candidateCanonical.id_oficial_tse);
      const candidateParty = normalizePoliticalLabel(getPoliticalPartyName(candidate, partidosCatalog), '');
      const candidateCargo = normalizeCargoKey(candidate.cargoKey || candidate.cargo);
      const canonicalMatch = targetCanonicalId && candidateCanonicalId && targetCanonicalId === candidateCanonicalId;
      const officialMatch = targetOfficialId && candidateOfficialId && targetOfficialId === candidateOfficialId;
      const nameMatch = targetName && candidateName && targetName === candidateName;
      const numberMatch = targetNumber && String(candidate.numero || '').trim() === targetNumber;
      const partyMatch = targetParty && candidateParty && normalizeText(targetParty) === normalizeText(candidateParty);
      const cargoMatch = targetCargo && candidateCargo && targetCargo === candidateCargo;

      if (canonicalMatch) {
        score += 65;
        reasons.push('identidade canonica');
      }
      if (officialMatch) {
        score += 55;
        reasons.push('identificador oficial');
      }
      if (nameMatch) {
        score += 30;
        reasons.push('nome normalizado');
      }
      if (cargoMatch) {
        score += 10;
        reasons.push('cargo compativel');
      }
      if (partyMatch) {
        score += 8;
        reasons.push('partido compativel');
      }
      if (numberMatch) {
        score += 7;
        reasons.push('numero igual');
      }

      if (!canonicalMatch && !officialMatch && !nameMatch) score = Math.min(score, numberMatch ? 20 : 0);

      return { candidate, score, reasons };
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score || Number(b.candidate.votos || 0) - Number(a.candidate.votos || 0));

  const best = scored[0];
  if (!best || best.score < 45) return null;
  const second = scored[1];
  if (second && best.score - second.score < 12
    && !best.reasons.includes('identidade canonica')
    && !best.reasons.includes('identificador oficial')) return null;

  return {
    candidate: best.candidate,
    score: best.score,
    confidence: best.score >= 75 ? 'alta' : best.score >= 55 ? 'media' : 'baixa',
    reasons: best.reasons,
  };
}

export function buildCandidateDossierData(dados, cargoKey, candidateIdOrNumero) {
  const agg = aggregateAll(dados, cargoKey ? { cargoKey } : {});
  const candidates = agg.candidatos || [];
  const candidate = candidates.find(item =>
    item.id === candidateIdOrNumero ||
    String(item.numero) === String(candidateIdOrNumero)
  );

  if (!candidate) {
    return { candidate: null, melhoresZonas: [], melhoresSecoes: [], adversarios: [], totalVotos: agg.totalVotosValidos || 0 };
  }

  const candidateById = new Map(candidates.map(item => [item.id, item]));
  const targetCargo = candidate.cargoKey || cargoKey;
  const targetId = candidate.id;
  const targetNumero = String(candidate.numero);
  const totalVotos = Number(agg.totalVotosValidos || 0);
  const rank = candidates.findIndex(item => item.id === targetId) + 1;
  const zonasMap = new Map();
  const melhoresSecoes = [];
  const secoesObj = dados?.secoes || dados || {};

  Object.entries(secoesObj).forEach(([key, sec]) => {
    const lista = sec?.[`raiox_${targetCargo}`];
    if (!Array.isArray(lista)) return;

    const zona = String(sec.zona || extractZonaFromKey(key) || 'N/D');
    const secao = String(sec.secao || extractSecaoFromKey(key) || key);
    const votosPorCandidato = {};
    let totalSecao = 0;
    let votosCandidato = 0;

    lista.forEach(item => {
      const numero = String(item.numero);
      const votos = Number(item.votos || 0);
      const id = `${targetCargo}_${numero}`;
      votosPorCandidato[id] = (votosPorCandidato[id] || 0) + votos;
      totalSecao += votos;
      if (numero === targetNumero) votosCandidato += votos;
    });

    if (!zonasMap.has(zona)) {
      zonasMap.set(zona, { zona, votos: 0, total: 0, votosPorCandidato: {}, secoes: 0 });
    }
    const zonaData = zonasMap.get(zona);
    zonaData.votos += votosCandidato;
    zonaData.total += totalSecao;
    zonaData.secoes += 1;
    Object.entries(votosPorCandidato).forEach(([id, votos]) => {
      zonaData.votosPorCandidato[id] = (zonaData.votosPorCandidato[id] || 0) + votos;
    });

    if (votosCandidato > 0) {
      const ordered = buildLocalCandidateOrder(votosPorCandidato, candidateById);
      melhoresSecoes.push({
        zona,
        secao,
        votos: votosCandidato,
        percentual: totalSecao > 0 ? (votosCandidato / totalSecao) * 100 : 0,
        rank: Math.max(1, ordered.findIndex(item => item.id === targetId) + 1),
        lider: ordered[0] || null,
        diferencaLider: Math.max(0, (ordered[0]?.votos || 0) - votosCandidato),
      });
    }
  });

  const melhoresZonas = Array.from(zonasMap.values())
    .filter(zona => zona.votos > 0)
    .map(zona => {
      const ordered = buildLocalCandidateOrder(zona.votosPorCandidato, candidateById);
      return {
        zona: zona.zona,
        votos: zona.votos,
        secoes: zona.secoes,
        percentual: zona.total > 0 ? (zona.votos / zona.total) * 100 : 0,
        rank: Math.max(1, ordered.findIndex(item => item.id === targetId) + 1),
        lider: ordered[0] || null,
        diferencaLider: Math.max(0, (ordered[0]?.votos || 0) - zona.votos),
      };
    })
    .sort((a, b) => b.votos - a.votos || Number(a.zona) - Number(b.zona) || String(a.zona).localeCompare(String(b.zona), 'pt-BR'));

  const adversarios = candidates
    .filter(item => item.id !== targetId)
    .slice(0, 5)
    .map(item => ({
      ...item,
      diferenca: Number(candidate.votos || 0) - Number(item.votos || 0),
      percentual: totalVotos > 0 ? (Number(item.votos || 0) / totalVotos) * 100 : 0,
    }));

  return {
    candidate: {
      ...candidate,
      rank,
      percentual: totalVotos > 0 ? (Number(candidate.votos || 0) / totalVotos) * 100 : 0,
    },
    totalVotos,
    totalSecoes: agg.totalSecoes || 0,
    melhoresZonas,
    bestZones: melhoresZonas,
    melhoresSecoes: melhoresSecoes.sort((a, b) => b.votos - a.votos || Number(a.secao) - Number(b.secao)).slice(0, 6),
    adversarios,
  };
}

function buildLocalCandidateOrder(votosPorCandidato, candidateById) {
  return Object.entries(votosPorCandidato || {})
    .map(([id, votos]) => ({
      ...(candidateById.get(id) || { id, nome: id, numero: id.split('_').pop() }),
      votos: Number(votos || 0),
    }))
    .sort((a, b) => b.votos - a.votos || a.nome.localeCompare(b.nome, 'pt-BR'));
}
